import { useCallback } from 'react'
import { Link, useParams } from 'react-router'

import { AskAboutThis } from '../../components/assistant/AskAboutThis'
import { usePublishAssistantPage } from '../../contexts/AssistantContext'
import { ASSISTANT_ENABLED } from '../../lib/features'
import { experimentDate, formatLocalClock } from '../../lib/format'
import { ComparisonContent } from '../../components/comparison/ComparisonContent'
import { comparisonScoreLabels, type ComparisonScoreLabels } from '../../components/comparison/ComparisonLabels'
import { PageHeader } from '../../components/PageHeader'
import { ProblemNotice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { useHomeSummary } from '../../contexts/HomeSummaryContext'
import { getComparison, getDailyStatus, getRecording } from '../../lib/api'
import { useResource } from '../../lib/useResource'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { NotFound } from '../NotFound'
import '../../styles/experiments-compare.css'

/** EXP-005: one comparison read per mount; account and route changes remount it. */
export function Compare() {
  const { a, b } = useParams<{ a: string; b: string }>()
  const { account } = useAuth()
  if (account?.experiments_enabled_for_me !== true || a === undefined || b === undefined) return <NotFound />
  return <CompareBody key={`${account?.user_id ?? 'none'}/${a}/${b}`} a={a} b={b} />
}

function CompareBody({ a, b }: { a: string; b: string }) {
  const { account } = useAuth()
  const assistantEnabled = ASSISTANT_ENABLED && account != null && 'experiments_enabled_for_me' in account && account.experiments_enabled_for_me === true
  const { state: home } = useHomeSummary()
  const display = home.kind === 'ready' ? home.summary.display : null
  const load = useCallback(async (signal: AbortSignal) => {
    const [result, before, after] = await Promise.all([
      getComparison(a, b, { signal }), getRecording(a, { signal }), getRecording(b, { signal }),
    ])
    if (result.schema_version != null && result.schema_version !== '1') {
      throw new Error('The comparison version is not supported.')
    }
    if (result.before_recording_id !== a || result.after_recording_id !== b
      || before.recording_id !== a || after.recording_id !== b) {
      throw new Error('The comparison did not match the requested readings.')
    }
    signal.throwIfAborted()
    let scoreLabels: ComparisonScoreLabels = {}
    if (result.category === 'comparable' && Object.keys(result.score_deltas ?? {}).length !== 0 && before.status === 'scored') {
      try {
        scoreLabels = comparisonScoreLabels(await getDailyStatus(a, { signal }), a)
      } catch {
        // Optional display metadata cannot discard a valid comparison.
        signal.throwIfAborted()
      }
    }
    signal.throwIfAborted()
    return { result, before, after, scoreLabels }
  }, [a, b])
  const read = useResource(load)
  const [landing, armLanding] = useRetryLanding(read.error !== null)
  usePublishAssistantPage(assistantEnabled && read.data ? {
    recordingId: read.data.before.recording_id,
    dateLabel: experimentDate(read.data.before.local_date ?? read.data.before.session_date),
    pairLabel: `${formatLocalClock(read.data.before.start_local_time)} → ${formatLocalClock(read.data.after.start_local_time)}`,
  } : null)

  if (read.isLoading || read.data === null) return <div className="stack stack--wide comparison">
    <PageHeader title="Compare" eyebrow="History · Compare" back={<Link className="page__back" to="/history">← History</Link>} />
    {read.error !== null
      ? <ProblemNotice error={read.error} onRetry={read.reload} retryBusy={read.isLoading} landing={armLanding}>This comparison could not be read.</ProblemNotice>
      : <p role="status">Loading the two readings…</p>}
  </div>

  return <ComparisonContent {...read.data} display={display} headingRef={landing} assistantAffordance={assistantEnabled && <AskAboutThis comparison />} />
}
